
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ServiceCard from "@/components/services/ServiceCard";
import { Link } from "react-router-dom";
import { Code, FileText, Network, Database, Share2, Shield } from "lucide-react";
import InquiryForm from "@/components/services/InquiryForm";
import { Database as SupabaseDatabase } from "@/integrations/supabase/types";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

type Service = SupabaseDatabase['public']['Tables']['services']['Row'];

export default function Services() {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const { data: services, isLoading, error } = useQuery({
    queryKey: ["services"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("id, title, description, slug, features");

      if (error) throw error;
      return data as Service[];
    }
  });

  const getIconForService = (title: string) => {
    switch (title.toLowerCase()) {
      case "software development":
        return <Code className="h-10 w-10 text-indigo-500" />;
      case "it infrastructure":
        return <Network className="h-10 w-10 text-blue-500" />;
      case "cloud services":
        return <Database className="h-10 w-10 text-purple-500" />;
      case "erp solutions":
        return <FileText className="h-10 w-10 text-green-500" />;
      case "cyber security":
        return <Shield className="h-10 w-10 text-red-500" />;
      default:
        return <Share2 className="h-10 w-10 text-gray-500" />;
    }
  };

  if (isLoading) {
    return <div className="text-center py-10">Loading services...</div>;
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Error fetching services. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="container mx-auto py-12">
      <section className="mb-16">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Our Services</h2>
          <p className="text-muted-foreground text-lg">
            Explore the wide range of services we offer to help your business
            succeed.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
          {services?.map((service) => (
            <ServiceCard
              key={service.id}
              title={service.title}
              description={service.description}
              icon={getIconForService(service.title)}
              features={service.features}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
